import { useCallback, useMemo } from 'react';

import useStatementReadiness from 'modules/Onboarding/AccountingSetup/StatementUpload/hooks/useStatementReadiness';
import useWaiverToasts from 'modules/Onboarding/AccountingSetup/StatementUpload/WaiverToasts';
import { missingPeriods, statementAccountLabel } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import useCompleteOnboardingStep from 'services/bookkeeping/mutations/useCompleteOnboardingStep';

interface StatementUploadCompletionOptions {
    /** False for a step that renders no statements section. See `useStatementReadiness`. */
    enabled?: boolean;
    /** True for a step whose only upload is the verified section. See `useStatementReadiness`. */
    ignoreAudience?: boolean;
}

interface CompletionWrites {
    /** The host step's own writes, made after waiver toasts are down and before the statement transition. */
    before?: () => Promise<void>;
    /** Whatever finishes the step once the statement transition is spent, typically navigation. */
    after: () => void | Promise<void>;
}

/**
 * Completing a step that renders `VerifiedStatementUploadSection`.
 *
 * The `statement_upload` transition dispatches the backend's transaction-extraction fan-out and
 * cannot be undone, which is what every rule here protects:
 *  - It fires only when the statements are *verified*. Readiness, the Continue gate, also passes on
 *    coverage the member could not act on, where the section shows nowhere to upload.
 *  - Waiver toasts come down before the first write. An Undo clicked after the fan-out reopens the
 *    step behind it, and a latch rather than a dismiss is needed because a waiver write already in
 *    flight lands later — see `WaiverToasts`.
 *  - On failure they come back only if the transition never committed. Once it has, the step is
 *    done and an Undo would reopen it, even when the throw came from `after`.
 *
 * `complete` rethrows, so the caller decides how a failure shows. Months still outstanding are the
 * caller's to confirm before calling it: a waiver unlocks Continue, it does not answer what is missing.
 */
export default function useStatementUploadCompletion({
    enabled = true,
    ignoreAudience = false,
}: StatementUploadCompletionOptions = {}) {
    const readiness = useStatementReadiness({ enabled, ignoreAudience });
    const { isVerified, outstandingAccounts } = readiness;
    const waiverToasts = useWaiverToasts();
    const completeStep = useCompleteOnboardingStep();

    const complete = useCallback(
        async ({ before, after }: CompletionWrites) => {
            let hasCompletedStatements = false;
            waiverToasts.suspend();

            try {
                await before?.();
                if (isVerified) {
                    await completeStep.mutateAsync({ step: 'statement_upload' });
                    hasCompletedStatements = true;
                }
                await after();
            } catch (error) {
                if (!hasCompletedStatements) {
                    waiverToasts.resume();
                }
                throw error;
            }
        },
        [isVerified, completeStep, waiverToasts]
    );

    /** What `IncompleteStatementsDialog` names: the account only when there is exactly one. */
    const incompleteDialogLabels = useMemo(
        () => ({
            accountLabel: outstandingAccounts.length === 1 ? statementAccountLabel(outstandingAccounts[0]) : undefined,
            missingPeriods: outstandingAccounts.flatMap((account) => missingPeriods(account)),
        }),
        [outstandingAccounts]
    );

    return { ...readiness, complete, incompleteDialogLabels };
}
